/**
 * Utility for parsing and formatting Solana transaction errors
 */

const LAMPORTS_PER_SOL = 1_000_000_000;

export interface InsufficientSOLError {
  type: 'insufficient_sol';
  haveLamports: number;
  needLamports: number;
  haveSOL: number;
  needSOL: number;
  shortfallLamports: number;
  shortfallSOL: number;
}

export interface ParsedSolanaError {
  type:
    | 'insufficient_sol'
    | 'insufficient_rent'
    | 'blockhash_expired'
    | 'transaction_timeout'
    | 'session_expired'
    | 'simulation_failed'
    | 'slippage_exceeded'
    | 'network_error'
    | 'unknown';
  message: string;
  details?: InsufficientSOLError;
  /** Whether this error is recoverable by retrying */
  isRetryable: boolean;
  /** Suggested action for the user */
  suggestedAction?: string;
}

/**
 * Parse a Solana transaction error to extract meaningful information
 *
 * Example error message:
 * "Simulation failed. Message: Transaction simulation failed: Error processing Instruction 1: custom program error: 0x1.
 *  Logs: [..., "Transfer: insufficient lamports 1096197, need 3546240", ...]"
 */
export function parseSolanaError(error: Error | string): ParsedSolanaError {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorString = String(errorMessage).toLowerCase();
  const originalMessage = typeof error === 'string' ? error : error.message;

  // Check for insufficient lamports error
  const insufficientMatch = errorString.match(/insufficient lamports (\d+),\s*need (\d+)/i);

  if (insufficientMatch) {
    const haveLamports = parseInt(insufficientMatch[1], 10);
    const needLamports = parseInt(insufficientMatch[2], 10);
    const shortfallLamports = needLamports - haveLamports;

    const haveSOL = haveLamports / LAMPORTS_PER_SOL;
    const needSOL = needLamports / LAMPORTS_PER_SOL;
    const shortfallSOL = shortfallLamports / LAMPORTS_PER_SOL;

    return {
      type: 'insufficient_sol',
      message: formatInsufficientSOLMessage(haveSOL, needSOL, shortfallSOL),
      isRetryable: false,
      suggestedAction: `Add at least ${formatSOL(shortfallSOL)} SOL to your wallet and try again.`,
      details: {
        type: 'insufficient_sol',
        haveLamports,
        needLamports,
        haveSOL,
        needSOL,
        shortfallLamports,
        shortfallSOL,
      },
    };
  }

  // Check for insufficient funds for rent
  if (errorString.includes('insufficient funds for rent')) {
    return {
      type: 'insufficient_rent',
      message: 'Your wallet doesn\'t have enough SOL to cover account rent. Please add more SOL to your wallet.',
      isRetryable: false,
      suggestedAction: 'Add at least 0.01 SOL to your wallet to cover rent costs.',
    };
  }

  // Check for blockhash expired/not found errors
  if (
    errorString.includes('blockhash not found') ||
    errorString.includes('block height exceeded') ||
    errorString.includes('blockhash expired')
  ) {
    return {
      type: 'blockhash_expired',
      message: 'The transaction took too long and expired. This can happen during network congestion.',
      isRetryable: true,
      suggestedAction: 'Please try again. If this keeps happening, try during off-peak hours.',
    };
  }

  // Check for transaction timeout errors
  if (
    errorString.includes('was not confirmed in') ||
    errorString.includes('transaction expired') ||
    errorString.includes('transactionexpiredtimeouterror')
  ) {
    return {
      type: 'transaction_timeout',
      message: 'The transaction wasn\'t confirmed in time. The Solana network may be congested.',
      isRetryable: true,
      suggestedAction: 'Please wait a moment and try again. Your funds are safe.',
    };
  }

  // Check for session/auth errors
  if (
    errorString.includes('unauthorized') ||
    errorString.includes('session expired') ||
    errorString.includes('jwt expired') ||
    errorString.includes('invalid token')
  ) {
    return {
      type: 'session_expired',
      message: 'Your session has expired. Please reconnect your wallet to continue.',
      isRetryable: false,
      suggestedAction: 'Disconnect and reconnect your wallet, then try again.',
    };
  }

  // Check for network errors
  if (
    errorString.includes('failed to fetch') ||
    errorString.includes('network error') ||
    errorString.includes('load failed') ||
    errorString.includes('request was aborted')
  ) {
    return {
      type: 'network_error',
      message: 'A network error occurred. Please check your internet connection.',
      isRetryable: true,
      suggestedAction: 'Check your internet connection and try again.',
    };
  }

  // Check for Jupiter slippage error (0x1771 = 6001)
  // This happens when token price moves too much during the swap
  if (
    errorString.includes('0x1771') ||
    errorString.includes('custom program error: 6001') ||
    errorString.includes('slippage tolerance exceeded') ||
    errorString.includes('exceeds desired slippage') ||
    errorString.includes('slippage error') ||
    (errorString.includes('jup') && errorString.includes('slippage'))
  ) {
    return {
      type: 'slippage_exceeded',
      message: 'The token price changed during the swap. This is normal market behavior.',
      isRetryable: true,
      suggestedAction: 'Please try again. The swap will use the latest price.',
    };
  }

  // Check if it's a simulation failure (generic - after checking specific cases)
  if (errorString.includes('simulation failed')) {
    return {
      type: 'simulation_failed',
      message: 'The transaction failed during simulation. This may be a temporary issue.',
      isRetryable: true,
      suggestedAction: 'Please try again. If the problem persists, contact support.',
    };
  }

  // Unknown error type
  return {
    type: 'unknown',
    message: originalMessage,
    isRetryable: false,
  };
}

/**
 * Format an insufficient SOL error message for display to users
 */
function formatInsufficientSOLMessage(
  haveSOL: number,
  needSOL: number,
  shortfallSOL: number
): string {
  return `Insufficient SOL balance. You need ${formatSOL(needSOL)} SOL but only have ${formatSOL(haveSOL)} SOL. Please add at least ${formatSOL(shortfallSOL)} SOL to your wallet.`;
}

/**
 * Format SOL amount with appropriate decimal places
 */
function formatSOL(sol: number): string {
  // Show more decimals for very small amounts
  if (sol < 0.001) {
    return sol.toFixed(6);
  }
  if (sol < 0.01) {
    return sol.toFixed(5);
  }
  if (sol < 1) {
    return sol.toFixed(4);
  }
  // For larger amounts, show fewer decimals
  return sol.toFixed(3);
}

/**
 * Check if an error is a user-actionable error that should be displayed prominently
 */
export function isUserActionableError(parsedError: ParsedSolanaError): boolean {
  return [
    'insufficient_sol',
    'insufficient_rent',
    'blockhash_expired',
    'transaction_timeout',
    'session_expired',
    'slippage_exceeded',
    'network_error',
  ].includes(parsedError.type);
}

/**
 * Get a user-friendly error message from any error
 */
export function getUserFriendlyErrorMessage(error: Error | string): string {
  const parsed = parseSolanaError(error);
  return parsed.message;
}
